import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, Trash2, User, ShieldAlert, Flag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { IdentityToggle } from './IdentityToggle';
import ReportCommentModal from './ReportCommentModal';

const CommentsSection = ({ postId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postAnon,   setPostAnon]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reportingId, setReportingId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchComments();
  }, [postId, user?.id]);

  const authHeaders = () => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/comments/${postId}`, {
        headers: { ...authHeaders() },
      });
      const data = await response.json();
      setComments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          postId,
          content:   newComment.trim(),
          anonymous: postAnon,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Server returns the shaped comment (anon fields masked already);
        // add a `mine=true` client-side so the delete affordance shows for
        // the author's own anon comment in this session.
        setComments([{ ...result.comment, mine: true, canDelete: true }, ...comments]);
        setNewComment('');
        setPostAnon(false);
      } else {
        alert('Failed to post comment');
      }
    } catch (error) {
      console.error('Failed to post comment:', error);
      alert('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (commentId, isModAction) => {
    const prompt = isModAction
      ? 'Delete this comment as a moderator?'
      : 'Delete this comment?';
    if (!window.confirm(prompt)) return;

    try {
      const response = await fetch(`${API_URL}/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: { ...authHeaders() },
      });

      if (response.ok) {
        setComments(comments.filter(c => c.id !== commentId));
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="bg-[#FFFCF3] border border-stone-200 rounded-2xl shadow-md p-5 mt-5">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-5 h-5 text-[#D6AD60]" />
        <h3 className="text-lg font-semibold text-stone-800">
          Comments ({comments.length})
        </h3>
      </div>

      {/* Comment Form */}
      {user ? (
        <form onSubmit={handleSubmit} className="mb-6" data-testid="comments-form">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center flex-shrink-0">
              {postAnon ? (
                <User className="w-5 h-5 text-[#1a1814] opacity-50" />
              ) : user.profilePicture ? (
                <img src={user.profilePicture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-[#1a1814]" />
              )}
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={postAnon ? 'Add a comment (anonymously)…' : 'Add a comment…'}
                className="w-full px-3 py-2 bg-[#E8DDCB] border border-[#D6AD60]/20 rounded-lg text-sm text-stone-800 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/40 resize-none"
                rows="3"
                maxLength="500"
                data-testid="comments-input"
              />
              <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                <IdentityToggle anonymous={postAnon} onChange={setPostAnon} name={user?.displayName || user?.name} testId="comments-anon-toggle" verb="Comment" />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-stone-500">{newComment.length}/500</span>
                  <button
                    type="submit"
                    disabled={!newComment.trim() || submitting}
                    className="px-4 py-2 btn-gold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-semibold"
                    data-testid="comments-submit"
                  >
                    <Send className="w-4 h-4" />
                    Post
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-6 p-4 bg-[#E8DDCB] rounded-lg text-center">
          <p className="text-stone-800">Sign in to leave a comment</p>
        </div>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {loading ? (
          <p className="text-center text-stone-500 py-4">Loading comments...</p>
        ) : comments.length === 0 ? (
          <p className="text-center text-stone-500 py-8">
            No comments yet. Be the first to share your thoughts!
          </p>
        ) : (
          comments.map(comment => {
            const isAnon = !!comment.anonymous;
            const mine   = !!comment.mine;
            const canDel = !!comment.canDelete;
            // Moderator-only when the caller can delete but is NOT the author
            // and NOT the anon owner — that means admin.
            const isMod  = canDel && !mine;
            // Show Report on other people's comments when the caller isn't
            // already able to delete (i.e., not the author or admin).
            const showReport = !!user && !mine && !canDel;
            return (
              <div key={comment.id} className="flex gap-3 p-3 bg-[#E8DDCB] rounded-lg" data-testid={`comment-${comment.id}`}>
                <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center flex-shrink-0">
                  {isAnon || !comment.userProfilePicture ? (
                    <User className="w-5 h-5 text-[#1a1814]" />
                  ) : (
                    <img src={comment.userProfilePicture} alt={comment.userName} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-medium text-stone-800 text-sm">
                      {comment.userName}
                    </span>
                    {isAnon && mine && (
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#D6AD60]/25 text-[#5b4727]">
                        anon · you
                      </span>
                    )}
                    <span className="text-xs text-stone-500">
                      {formatTimeAgo(comment.createdAt)}
                    </span>
                  </div>
                  <p className="text-stone-700 text-sm break-words whitespace-pre-line">
                    {comment.content}
                  </p>
                </div>
                {canDel && (
                  <button
                    type="button"
                    onClick={() => deleteComment(comment.id, isMod)}
                    aria-label={isMod ? 'Delete as moderator' : 'Delete comment'}
                    title={isMod ? 'Delete as moderator' : 'Delete comment'}
                    className={`transition-colors ${isMod ? 'text-orange-500 hover:text-orange-700' : 'text-stone-500 hover:text-red-400'}`}
                    data-testid={`comment-delete-${comment.id}`}
                  >
                    {isMod ? <ShieldAlert className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
                {showReport && (
                  <button
                    type="button"
                    onClick={() => setReportingId(comment.id)}
                    aria-label="Report comment"
                    title="Report to moderators"
                    className="text-stone-400 hover:text-orange-500 transition-colors"
                    data-testid={`comment-report-${comment.id}`}
                  >
                    <Flag className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <ReportCommentModal
        open={!!reportingId}
        commentId={reportingId}
        onClose={() => setReportingId(null)}
      />
    </div>
  );
};

export default CommentsSection;
